import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {FieldType, MemberAddeditComponent, MemberAddeditDialogModel} from '../member-addedit/member-addedit.component';
import {VendorGroupsProductsService} from '../../../services/vendor-groups-products/vendor-groups-products.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {UiAlertService} from '../../../services/ui-alert/ui-alert.service';
import {VendorGroupsRatesService} from '../../../services/vendor-groups-rates/vendor-groups-rates.service';
import {TeamCompaniesService} from '../../../services/teamcompanies/teamcompanies.service';
import {UtilsService} from '../../../services/utils/utils.service';
import {MatSort} from '@angular/material/sort';
import {fromEvent} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';

@Component({
  selector: 'app-vendor-groups-rates',
  templateUrl: './vendor-groups-rates.component.html',
  styleUrls: ['./vendor-groups-rates.component.scss']
})
export class VendorGroupsRatesComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild('search') search: ElementRef | undefined;

  protected allColumnNames = ['vendorProductId', 'companyId', 'tiers', 'metadata', 'actionmenu'];
  protected items = new MatTableDataSource<any>();
  private companies: any;
  private products: any;

  private addEditFields = [
    {name: 'vendorProductId', label: 'Vendor Product', fieldType: FieldType.F_DROPDOWN, values: [], required: true},
    {name: 'companyId', label: 'Company', fieldType: FieldType.F_DROPDOWN, values: [], required: true},
    {name: 'metadata', label: 'Configuration JSON', fieldType: FieldType.F_JSON, required: true},
  ];

  constructor(
      private vendorGroupsProductsService: VendorGroupsProductsService,
      private dialog: MatDialog,
      private snackBar: MatSnackBar,
      private uiAlertService: UiAlertService,
      private vendorGroupsRatesService: VendorGroupsRatesService,
      private teamCompaniesService: TeamCompaniesService,
      private activatedRoute: ActivatedRoute,
      private router: Router
  ) {

  }

  ngOnInit(): void {
    this.reloadItems();
  }

  ngAfterViewInit(): void {
    this.activatedRoute.queryParamMap.subscribe(params => {
      // @ts-ignore
      this.search.nativeElement.value = params.get('search') || '';
      this.reloadItems();
    });
    fromEvent(this.search?.nativeElement, 'keyup').pipe(
        filter(Boolean),
        debounceTime(300),
        distinctUntilChanged(),
        tap((): void => {
          this.reloadItems();
          this.router.navigate([] , {
            relativeTo: this.activatedRoute,
            queryParams: {search: this.search?.nativeElement.value ?? ''},
            queryParamsHandling: 'merge'
          });
        })).subscribe();
  }

  private reloadItems(): void {
    this.vendorGroupsRatesService.getAllVendorGroupRates(10000, 0, this.search?.nativeElement.value ?? '').then(rates => {
      this.items.data = rates;
      this.items.sort = this.sort;
    });
    this.vendorGroupsProductsService.getAllVendorGroupProducts().then(products => {
      this.products = products;
      this.addEditFields[0].values = products.map((vendor: any) => `${vendor.vendorProductName}:${vendor.id}`);
    });
    this.teamCompaniesService.getCompanies().then(companies => {
      this.companies = companies;
      this.addEditFields[1].values = companies.filter((company: { type: string; }) => company.type === 'employer').map((company: any) => `${company.name}:${company.id}`);
    });
  }

  getTiers(item: any): string {
    return((item.metadata.tiers || item.metadata.compact.tiers).map((tier: { tier: any; }) => tier.tier).join(', '));
  }

  getCompanyName(item: any): string {
    const thisCompany = this.companies?.find((company: any) => company.id === item.companyId);
    return thisCompany ? thisCompany.name : '';
  }

  getVendorProductName(item: any): string {
   const product = this.products?.find((vendorProduct: { id: any; }) => vendorProduct.id === item.vendorProductId);
   return product ? `${product.vendorProductName} ${product.metadata.name} (${product.metadata.shortName})` : '';
  }

  addItem(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(null, this.addEditFields, 'Group Rate')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.vendorGroupsRatesService.createVendorGroupRate(dialogResult.vendorProductId, dialogResult.companyId, dialogResult.metadata).then(() => {
          this.reloadItems();
          this.snackBar.open('New group rate created');
        });
      }
    });
  }

  editItem(item: any): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(item, this.addEditFields, 'Group Rate')
    });
    dialogRef.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.vendorGroupsRatesService.modifyVendorGroupRate(item.id, dialogResult).then(() => {
          this.reloadItems();
          this.snackBar.open('Group rate updated');
        });
      }
    });
  }

  duplicateItem(item: any): void {
    this.vendorGroupsRatesService.createVendorGroupRate(item.vendorProductId, item.companyId, item.metadata).then(() => {
      this.reloadItems();
      this.snackBar.open('Duplicate group rate created');
    });
  }

  removeItem(item: any): void {
    this.uiAlertService.presentAlertConfirm('Do you really want to remove this group rate?').then(ok => {
      this.vendorGroupsRatesService.deleteVendorGroupRate(item.id).then(() => {
        this.reloadItems();
        this.snackBar.open('Group rate removed');
      });
    });
  }

  truncateJSONString(str: string, maxLength: number): string {
    str = JSON.stringify(str);
    return str.length <= maxLength ? str : str.slice(0, maxLength - 3) + '...';
  }

}
